import {
    Alert,
    Box,
    Chip,
    Divider,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import React, { useMemo } from 'react';

import { MessageKey, useI18n } from '../../i18n';
import { SavedChart } from '../tarotSlice';

import { Chart, computeChart } from './chart';
import { ChartWheel } from './ChartWheel';
import { interpretChart, Position } from './interpret';
import { BODY_GLYPHS, SIGN_GLYPHS, SIGNS } from './zodiac';

const formatOffset = (minutes: number): string => {
    const sign = minutes < 0 ? '-' : '+';
    const abs = Math.abs(minutes);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${sign}${p(Math.floor(abs / 60))}:${p(abs % 60)}`;
};

const glyphOf = (id: Position['id']): string =>
    id === 'asc' ? 'AC' : id === 'mc' ? 'MC' : BODY_GLYPHS[id];

const degreeText = (p: Position): string =>
    `${p.degree}°${String(p.minute).padStart(2, '0')}'`;

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => (
    <Typography
        variant="overline"
        color="primary"
        component="h2"
        sx={{ letterSpacing: '0.3em', display: 'block', mt: 3, mb: 1 }}
    >
        {children}
    </Typography>
);

/** A saved birth chart: the wheel, the big three, every placement and the aspects. */
export const ChartView: React.FC<{ saved: SavedChart }> = ({ saved }) => {
    const { t, language, locale } = useI18n();
    const result = useMemo(() => {
        try {
            const chart: Chart = computeChart(saved.input);
            return { chart, reading: interpretChart(chart, language) };
        } catch {
            return null;
        }
    }, [saved.input, language]);

    if (!result) return <Alert severity="error">{t('chart.error.calc')}</Alert>;
    const { chart, reading } = result;
    const { input } = chart;

    const date = new Date(
        input.year,
        input.month - 1,
        input.day
    ).toLocaleDateString(locale, { dateStyle: 'long' });
    const born = input.time
        ? t('chart.born', {
              date,
              place: input.place.name,
              time: `${String(input.time.hour).padStart(2, '0')}:${String(
                  input.time.minute
              ).padStart(2, '0')}`,
          })
        : t('chart.bornNoTime', { date, place: input.place.name });

    const warnings = [
        ...(input.time ? [] : (['noTime'] as const)),
        ...chart.warnings,
    ];

    return (
        <Box>
            <Stack alignItems="center" textAlign="center" spacing={0.5}>
                <Typography variant="h4" component="h1">
                    {saved.name
                        ? t('chart.heading', { name: saved.name })
                        : t('chart.title')}
                </Typography>
                <Typography color="text.secondary">{born}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {t('chart.zone', {
                        offset: formatOffset(chart.offsetMinutes),
                        zone: input.place.zone,
                    })}
                    {chart.houseSystem &&
                        ` · ${t(`chart.system.${chart.houseSystem}` as MessageKey)}`}
                </Typography>
            </Stack>

            <Stack alignItems="center" sx={{ my: 2 }}>
                <ChartWheel chart={chart} label={t('chart.wheel')} />
            </Stack>

            {warnings.length > 0 && (
                <Stack spacing={1} sx={{ mb: 1 }}>
                    {warnings.map((w) => (
                        <Alert key={w} severity="info">
                            {t(`chart.warn.${w}` as MessageKey)}
                        </Alert>
                    ))}
                </Stack>
            )}

            <SectionTitle>{t('chart.bigThree')}</SectionTitle>
            <Stack spacing={1.5}>
                {reading.bigThree.map(({ kind, position, text }) => (
                    <Paper
                        key={kind}
                        sx={{ p: 2, background: 'rgba(21,14,36,.85)' }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="baseline"
                            flexWrap="wrap"
                        >
                            <Typography
                                variant="h6"
                                component="h3"
                                sx={{ fontFamily: 'Cinzel, serif' }}
                            >
                                {t(`chart.${kind}` as MessageKey)}{' '}
                                <Box component="span" aria-hidden>
                                    {
                                        SIGN_GLYPHS[
                                            SIGNS.indexOf(position.signId)
                                        ]
                                    }
                                </Box>{' '}
                                {position.sign}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {degreeText(position)}
                            </Typography>
                        </Stack>
                        <Typography sx={{ mt: 0.5 }}>{text}</Typography>
                    </Paper>
                ))}
            </Stack>

            {reading.ruler && (
                <>
                    <SectionTitle>{t('chart.ruler')}</SectionTitle>
                    <Paper sx={{ p: 2, background: 'rgba(21,14,36,.85)' }}>
                        <Typography>{reading.ruler.text}</Typography>
                    </Paper>
                </>
            )}

            <SectionTitle>{t('chart.placements')}</SectionTitle>
            <Paper sx={{ background: 'rgba(21,14,36,.85)' }}>
                {reading.placements.map((p, i) => (
                    <Box key={p.id}>
                        {i > 0 && <Divider />}
                        <Box sx={{ p: 1.5 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                                rowGap={0.5}
                            >
                                <Typography
                                    component="span"
                                    aria-hidden
                                    sx={{ fontSize: '1.2rem', width: 24 }}
                                >
                                    {glyphOf(p.id)}
                                </Typography>
                                <Typography
                                    component="h3"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {p.name} {reading.text.in} {p.sign}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {degreeText(p)}
                                </Typography>
                                {p.retrograde && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        label={t('chart.retrograde')}
                                    />
                                )}
                                {p.house && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={t('chart.house', { n: p.house })}
                                    />
                                )}
                            </Stack>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                {p.text.charAt(0).toUpperCase() +
                                    p.text.slice(1)}
                            </Typography>
                            {p.house && p.houseText && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                >
                                    {p.houseText}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ))}
            </Paper>

            {reading.angles.length > 0 && (
                <>
                    <SectionTitle>{t('chart.angles')}</SectionTitle>
                    <Paper sx={{ background: 'rgba(21,14,36,.85)' }}>
                        {reading.angles.map((p, i) => (
                            <Box key={p.id}>
                                {i > 0 && <Divider />}
                                <Box sx={{ p: 1.5 }}>
                                    <Typography sx={{ fontWeight: 600 }}>
                                        {p.name} {reading.text.in} {p.sign}{' '}
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {degreeText(p)}
                                        </Typography>
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {reading.text.bodies[p.id].theme}
                                    </Typography>
                                    {p.id === 'mc' && reading.angleTexts.mc && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.5 }}
                                        >
                                            {reading.angleTexts.mc}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Paper>
                </>
            )}

            {reading.aspects.length > 0 && (
                <>
                    <SectionTitle>{t('chart.aspects')}</SectionTitle>
                    <Paper sx={{ background: 'rgba(21,14,36,.85)', p: 1.5 }}>
                        <Stack spacing={0.75}>
                            {reading.aspects.map((a) => (
                                <Box key={`${a.title}-${a.type}`}>
                                    <Typography variant="body2">
                                        <strong>{a.title}</strong>{' '}
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            ({a.typeName}, {a.orb.toFixed(1)}°)
                                        </Typography>
                                    </Typography>
                                    {a.note && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {a.note}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    </Paper>
                </>
            )}

            <Typography
                variant="caption"
                color="text.secondary"
                component="p"
                sx={{ mt: 3 }}
            >
                {t('chart.accuracy')}
            </Typography>
        </Box>
    );
};
